import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleUp, faDownload } from '@fortawesome/free-solid-svg-icons'
import Table, {
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
} from 'components/Table'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import Pagination from 'components/Pagination'
import Link from 'components/Link'
import { Routes } from 'routes'
import { DATE_FORMAT, DATE_HOUR_FORMAT, ITEMS_PER_PAGE } from 'constant'
import { Agency } from 'types/agency'
import { Disposition } from 'types/disposition'
import DateUtils from 'utils/date'
import TsxUtils from 'utils/tsx'
import DispositionDetails from '../DispositionDetails'
import InvoiceActions from '../InvoiceActions'
import service from '../service'

const TABLE_COLUMNS_COUNT = 9

interface DispositionsListProps {
  agencyId: Agency['id']
}

const DispositionsList: React.FC<DispositionsListProps> = ({ agencyId }) => {
  const { t } = useTranslation()
  const [dispositions, setDispositions] = useState<Disposition[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [expandedIds, setExpandedIds] = useState<string[]>([])

  const getDispositions = (): Promise<void> =>
    service.getDispositions(agencyId, pageNumber, setDispositions, setTotalCount, setLoading)

  useEffect(() => {
    void getDispositions()
  }, [agencyId, pageNumber])

  const toggleExpanded = (id: Disposition['id']): void =>
    setExpandedIds(
      expandedIds.includes(id) ? expandedIds.filter(el => el !== id) : [...expandedIds, id]
    )

  const rows: ReactNode = dispositions.map(disposition => {
    const unsentCount = service.unsentCorrections(disposition).length
    const isExpanded = expandedIds.includes(disposition.id)

    return (
      <React.Fragment key={disposition.id}>
        <TableRow className='DispositionsList__row'>
          <TableData>{DateUtils.parseAndFormat(disposition.createdAt, DATE_FORMAT)}</TableData>
          <TableData>
            <Link toUrl={Routes.CAMPAIGNS.ONE(disposition.campaignGqlId)}>
              {disposition.productName}
            </Link>
          </TableData>
          <TableData>
            <div>{disposition.customerName}</div>
            <div className='DispositionsList__nip'>{disposition.nip}</div>
          </TableData>
          <TableData>{service.formatPrice(disposition.price)}</TableData>
          <TableData>
            <Button
              dataCy='disposition-download'
              size={ButtonSize.SMALL}
              theme={ButtonTheme.NAVY_BLUE_OUTLINE}
              icon={faDownload}
              tooltip={t('agencies.dispositions.downloadDisposition')}
              disabled={!disposition.fileUrl}
              onClick={() =>
                service.downloadFile(disposition.fileUrl, service.dispositionFileName(disposition))
              }
            />
          </TableData>
          <TableData>
            <InvoiceActions
              disposition={disposition}
              onChanged={() => void getDispositions()}
            />
          </TableData>
          <TableData>
            {disposition.corrections.length}
            {unsentCount > 0 && (
              <span className='DispositionsList__unsent'>
                {t('agencies.dispositions.unsentCount', { number: unsentCount })}
              </span>
            )}
          </TableData>
          <TableData>
            {disposition.sentAt ? (
              t('agencies.dispositions.sentAt', {
                date: DateUtils.parseAndFormat(disposition.sentAt, DATE_HOUR_FORMAT),
              })
            ) : (
              <span className='DispositionsList__unsent'>{t('agencies.dispositions.notSent')}</span>
            )}
          </TableData>
          <TableData>
            <div
              data-cy='disposition-expand'
              className='DispositionsList__collapse-btn'
              onClick={() => toggleExpanded(disposition.id)}
            >
              <FontAwesomeIcon
                icon={faAngleUp}
                className={
                  'DispositionsList__collapse-icon' +
                  TsxUtils.extraStyle(!isExpanded, 'DispositionsList__collapse-icon--collapsed')
                }
              />
            </div>
          </TableData>
        </TableRow>

        {isExpanded && (
          <TableRow>
            <td
              colSpan={TABLE_COLUMNS_COUNT}
              className='DispositionsList__details'
            >
              <DispositionDetails
                disposition={disposition}
                onChanged={() => void getDispositions()}
              />
            </td>
          </TableRow>
        )}
      </React.Fragment>
    )
  })

  if (!loading && dispositions.length === 0) {
    return <div className='DispositionsList__empty'>{t('agencies.dispositions.empty')}</div>
  }

  return (
    <>
      <Table
        className='DispositionsList'
        loading={loading}
      >
        <TableHeader>
          <TableHeaderItem>{t('agencies.dispositions.date')}</TableHeaderItem>
          <TableHeaderItem>{t('common.campaign')}</TableHeaderItem>
          <TableHeaderItem>{t('agencies.dispositions.customerName')}</TableHeaderItem>
          <TableHeaderItem>{t('agencies.dispositions.price')}</TableHeaderItem>
          <TableHeaderItem>{t('agencies.dispositions.disposition')}</TableHeaderItem>
          <TableHeaderItem>{t('agencies.dispositions.invoice')}</TableHeaderItem>
          <TableHeaderItem>{t('agencies.dispositions.corrections.header')}</TableHeaderItem>
          <TableHeaderItem>{t('common.status')}</TableHeaderItem>
          <TableHeaderItem />
        </TableHeader>

        <TableBody>
          <>{rows}</>
        </TableBody>
      </Table>

      <Pagination
        activePage={pageNumber}
        itemsCountPerPage={ITEMS_PER_PAGE}
        totalItemsCount={totalCount}
        onChange={setPageNumber}
      />
    </>
  )
}

export default DispositionsList
